import React, { useEffect, useRef, useState } from "react";
import AuthenticityTokenField from "../../../components/forms/AuthenticityTokenField";
import { AddressAutocompleteField } from "../../../components/forms/AddressAutocompleteField";
import { JobPhotoUploadField } from "../../../components/forms/JobPhotoUploadField";
import { DashboardPageLayout } from "../../../components/layout/DashboardPageLayout";
import { SiteGeofenceMap } from "../../../components/maps/SiteGeofenceMap";
import { ImageLightbox } from "../../../components/ui/ImageLightbox";
import { Button } from "../../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { FieldLabel, TextInput } from "../../../components/ui/FormControls";
import { JobSiteStatusCard } from "../../../components/dashboard/JobSiteStatusCard";
import { dashboardJobHref } from "../../../lib/dashboardJobHref";
import { ssrFormattedDateOr } from "../../../lib/ssrFormattedDate";
import type { JobDetail, JobPhotoItem, MainAppPageProps } from "../types";

type JobShowPageProps = Pick<
  MainAppPageProps,
  "csrfToken" | "googleMapsApiKey" | "routes" | "jobForm" | "canAccessBackend"
> & {
  job: JobDetail;
  jobPhotos?: JobPhotoItem[];
};

export const JobShowPage: React.FC<JobShowPageProps> = ({
  csrfToken,
  googleMapsApiKey,
  routes,
  job,
  jobForm,
  jobPhotos = [],
  canAccessBackend = false,
}) => {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [coords, setCoords] = useState<{ lat: number; lng: number }>({
    lat: job.lat,
    lng: job.lng,
  });
  const [localPhotos, setLocalPhotos] = useState<JobPhotoItem[]>([]);
  const localPhotoUrlsRef = useRef<string[]>([]);
  const nextLocalPhotoId = useRef(0);

  useEffect(() => {
    return () => {
      localPhotoUrlsRef.current.forEach((url) => {
        URL.revokeObjectURL(url);
      });
    };
  }, []);

  const handleUploaded = (files: File[]) => {
    const next: JobPhotoItem[] = files.map((file) => {
      const url = URL.createObjectURL(file);
      localPhotoUrlsRef.current.push(url);
      nextLocalPhotoId.current += 1;
      const isVideo = file.type.startsWith("video/");
      return {
        id: `local-${nextLocalPhotoId.current}`,
        url,
        thumbnailUrl: isVideo ? null : url,
        mediaType: isVideo ? "video" : "photo",
        takenAtDisplay: null,
      };
    });
    setLocalPhotos((prev) => [...prev, ...next]);
  };

  const jobsPath = routes.dashboardJobs ?? "/dashboard/jobs";
  const jobPath = dashboardJobHref(routes, job.id);
  const canManageJobs = Boolean(job.canManageJobs);
  const allPhotos = [...jobPhotos, ...localPhotos];
  const photos = allPhotos.filter((item) => item.mediaType !== "video");
  const videos = allPhotos.filter((item) => item.mediaType === "video");
  const lightboxImages = photos.map((item) => ({
    src: item.url,
    alt: job.name,
  }));

  return (
    <DashboardPageLayout
      trail={[{ label: "Jobs", href: jobsPath }, { label: job.name }]}
      title={job.name}
      subtitle={job.address || undefined}
    >
      <div className="flex flex-col gap-6">
        <Card className="bg-card">
          <CardHeader>
            <CardTitle className="text-base">Pictures</CardTitle>
            {job.createdAtDisplay ? (
              <CardDescription>
                Added {ssrFormattedDateOr(job.createdAtDisplay)}.
              </CardDescription>
            ) : null}
          </CardHeader>
          <CardContent className="flex flex-col gap-6">
            {canAccessBackend ? (
              <JobPhotoUploadField
                jobId={job.id}
                csrfToken={csrfToken}
                routes={routes}
                onUploaded={handleUploaded}
              />
            ) : null}
            {photos.length === 0 && videos.length === 0 ? (
              <p className="text-sm text-muted-foreground">No pictures yet</p>
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {photos.map((photo, index) => (
                  <button
                    key={photo.id}
                    type="button"
                    className="overflow-hidden rounded-xl border border-border bg-muted"
                    onClick={() => setLightboxIndex(index)}
                  >
                    <img
                      src={photo.thumbnailUrl || photo.url}
                      alt={`${job.name} photo`}
                      className="aspect-square h-full w-full object-cover"
                    />
                  </button>
                ))}
                {videos.map((video) => (
                  <video
                    key={video.id}
                    src={video.url}
                    controls
                    className="aspect-square w-full rounded-xl border border-border bg-black object-cover"
                    poster={video.thumbnailUrl ?? undefined}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {canManageJobs ? (
          <JobSiteStatusCard
            job={job}
            jobPath={jobPath}
            csrfToken={csrfToken}
          />
        ) : null}

        {canManageJobs ? (
          <Card className="bg-card">
            <CardHeader>
              <CardTitle className="text-base">Site area</CardTitle>
            </CardHeader>
            <CardContent>
              <form
                action={jobPath}
                method="post"
                className="flex max-w-xl flex-col gap-5"
              >
                <AuthenticityTokenField csrfToken={csrfToken} />
                <input type="hidden" name="_method" value="patch" />
                <div>
                  <FieldLabel htmlFor="job_name">Job name</FieldLabel>
                  <TextInput
                    id="job_name"
                    name="job[name]"
                    defaultValue={jobForm?.name ?? job.name}
                    required
                  />
                </div>
                <AddressAutocompleteField
                  id="job_address"
                  label="Address"
                  addressFieldName="job[address]"
                  placeIdFieldName="job[place_id]"
                  latFieldName="job[lat]"
                  lngFieldName="job[lng]"
                  defaultValue={jobForm?.address ?? job.address ?? ""}
                  defaultLat={jobForm?.lat ?? job.lat}
                  defaultLng={jobForm?.lng ?? job.lng}
                  googleMapsApiKey={googleMapsApiKey}
                  onResolved={(location) => {
                    if (location) {
                      setCoords({ lat: location.lat, lng: location.lng });
                    }
                  }}
                />
                <SiteGeofenceMap
                  googleMapsApiKey={googleMapsApiKey}
                  lat={coords.lat}
                  lng={coords.lng}
                  defaultRadiusM={
                    jobForm?.geofenceRadiusM ?? job.geofenceRadiusM
                  }
                  includeCoordinateFields={false}
                />
                <Button type="submit" variant="premium">
                  Save site area
                </Button>
              </form>
            </CardContent>
          </Card>
        ) : null}
      </div>

      {lightboxIndex !== null && lightboxImages.length > 0 ? (
        <ImageLightbox
          images={lightboxImages}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
        />
      ) : null}
    </DashboardPageLayout>
  );
};
